import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

function toVector3(value, fallback = [0, 0, 0]) {
  if (Array.isArray(value) && value.length === 3) {
    return value.map((v, index) => {
      const parsed = Number(v);
      return Number.isFinite(parsed) ? parsed : fallback[index];
    });
  }
  return fallback;
}

function toRotation(value) {
  if (Array.isArray(value) && value.length === 3) {
    return toVector3(value, [0, 0, 0]);
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return [0, parsed, 0];
  }
  return [0, 0, 0];
}

function parseLamellaColor(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new THREE.Color(value);
  }

  if (typeof value !== 'string') {
    return new THREE.Color('#808080');
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return new THREE.Color('#808080');
  }

  if (trimmed.startsWith('#')) {
    return new THREE.Color(trimmed);
  }

  if (/^0x[0-9a-fA-F]+$/.test(trimmed)) {
    return new THREE.Color(Number(trimmed));
  }

  if (/^[0-9a-fA-F]{6,8}$/.test(trimmed)) {
    return new THREE.Color(`#${trimmed.slice(0, 6)}`);
  }

  return new THREE.Color('#808080');
}

const BATCH_ASSETS = {
  wood: { geometryUrl: '/WoodOutlinedColored.glb', geometryNode: 'Cube', materialUrl: '/WoodOutlinedColored.glb', materialNode: 'Cube', instanceColors: true, tuneWoodMaterial: true, shadows: true },
  textured: { geometryUrl: '/WoodOutlinedColored.glb', geometryNode: 'Cube', materialUrl: '/les.glb', materialNode: 'lamela', materialName: 'lamela', shadows: true },
  outlined: { geometryUrl: '/WoodOutlined.glb', geometryNode: 'Cube', materialUrl: '/WoodOutlined.glb', materialNode: 'Cube', materialName: 'Material.001' },
  insulation: { geometryUrl: '/InsulationTest.glb', geometryNode: 'Cube', materialUrl: '/InsulationTest.glb', materialNode: 'Cube', materialName: 'Material.002' },
  base: { geometryUrl: '/lamellaBase.glb', geometryNode: 'Cube', materialUrl: '/lamellaBase.glb', materialNode: 'Cube', materialName: 'Material.002' },
  ready: { geometryUrl: '/lamellaReady.glb', geometryNode: 'Cube', materialUrl: '/lamellaReady.glb', materialNode: 'Cube', materialName: 'Material.002' }
};

export function InstancedLamellaBatch({ instances = [], variant = 'wood', transparent = false, opacity = 1, polygonOffset = false, polygonOffsetFactor = 1, polygonOffsetUnits = 1 }) {
  const asset = BATCH_ASSETS[variant] || BATCH_ASSETS.wood;
  const geometrySource = useGLTF(asset.geometryUrl);
  const materialSource = useGLTF(asset.materialUrl);
  const geometry = geometrySource.nodes?.[asset.geometryNode]?.geometry;
  const baseMaterial = materialSource.materials?.[asset.materialName] || materialSource.nodes?.[asset.materialNode]?.material;
  const meshRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const material = useMemo(() => {
    if (!baseMaterial) return null;
    const cloned = baseMaterial.clone();

    if (asset.tuneWoodMaterial) {
      cloned.transparent = true;
      cloned.opacity = 1;
      cloned.fog = false;
      if (cloned.metalness !== undefined) cloned.metalness = cloned.metalness ?? 0.5;
      if (cloned.roughness !== undefined) cloned.roughness = cloned.roughness ?? 0.6;
      // Instance colors work via USE_INSTANCING_COLOR (setColorAt), NOT vertexColors.
      // vertexColors reads from geometry.attributes.color which doesn't exist → black.
      cloned.vertexColors = false;
      // White base so instanceColor multiplies to the true per-instance color.
      cloned.color = new THREE.Color(0xffffff);
      // Match the subtle emissive from WoodOutlinedColoredBeam's ObjectHighlighter
      cloned.emissiveIntensity = 0.07;
    }

    if (transparent) {
      cloned.transparent = true;
      cloned.opacity = opacity;
      cloned.depthWrite = false;
    }

    if (polygonOffset) {
      cloned.polygonOffset = true;
      cloned.polygonOffsetFactor = polygonOffsetFactor ?? 1;
      cloned.polygonOffsetUnits = polygonOffsetUnits ?? 1;
    }

    cloned.needsUpdate = true;
    return cloned;
  }, [asset.tuneWoodMaterial, baseMaterial, opacity, polygonOffset, polygonOffsetFactor, polygonOffsetUnits, transparent]);

  useEffect(() => {
    return () => {
      material?.dispose?.();
    };
  }, [material]);

  useLayoutEffect(() => {
    if (!meshRef.current) return;

    const mesh = meshRef.current;
    instances.forEach((instance, index) => {
      const position = toVector3(instance?.position, [0, 0, 0]);
      const rotation = toRotation(instance?.rotation);
      const scale = toVector3(instance?.scale, [1, 1, 1]);

      dummy.position.set(position[0], position[1], position[2]);
      dummy.rotation.set(rotation[0], rotation[1], rotation[2]);
      dummy.scale.set(scale[0], scale[1], scale[2]);
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);

      if (asset.instanceColors) {
        mesh.setColorAt(index, parseLamellaColor(instance?.color));
      }
    });

    mesh.instanceMatrix.needsUpdate = true;
    if (asset.instanceColors && mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true;
    }
    if (mesh.computeBoundingSphere) {
      mesh.computeBoundingSphere();
    }
  }, [asset.instanceColors, dummy, instances]);

  if (!geometry || !material || !instances.length) {
    return null;
  }

  return <instancedMesh ref={meshRef} args={[geometry, material, instances.length]} castShadow={!!asset.shadows} receiveShadow={!!asset.shadows} />;
}

useGLTF.preload('/WoodOutlinedColored.glb');
useGLTF.preload('/les.glb');
